import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [feedback, setFeedback] = useState([]);
    const [users, setUsers] = useState([]);
    const [matchingRules, setMatchingRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageBlobs, setImageBlobs] = useState({});
    const [showCreateRuleForm, setShowCreateRuleForm] = useState(false);
    const [newRule, setNewRule] = useState({
        field_name: 'projectName',
        input_pattern: '',
        mapped_value: '',
        description: ''
    });
    const { getAuthHeaders, startImpersonation } = useAuth();

    useEffect(() => {
        fetchAdminData();
        
        // Cleanup blob URLs on unmount
        return () => {
            Object.values(imageBlobs).forEach(blobUrl => {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
            });
        };
    }, []);
    
    // Cleanup blob URLs when imageBlobs changes
    useEffect(() => {
        return () => {
            Object.values(imageBlobs).forEach(blobUrl => {
                if (blobUrl) {
                    URL.revokeObjectURL(blobUrl);
                }
            });
        };
    }, [imageBlobs]);

    const fetchImageBlob = async (filename) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/feedback/attachments/${filename}`, {
                headers: authHeaders
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setImageBlobs(prev => ({
                    ...prev,
                    [filename]: blobUrl
                }));
                return blobUrl;
            } else {
                console.error('Failed to fetch image:', filename, response.status);
                return null;
            }
        } catch (error) {
            console.error('Error fetching image blob:', error);
            return null;
        }
    };

    const downloadAttachment = async (filename, originalTitle) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/feedback/attachments/${filename}`, {
                headers: authHeaders
            });
            
            if (response.ok) {
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = `feedback-${originalTitle.replace(/[^a-z0-9]/gi, '_')}-${filename}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                
                URL.revokeObjectURL(url);
            } else {
                console.error('Failed to download attachment:', filename, response.status);
                alert('Failed to download attachment');
            }
        } catch (error) {
            console.error('Error downloading attachment:', error);
            alert('Error downloading attachment');
        }
    };

    const previewAttachment = (blobUrl, title) => {
        // Create a new window with an HTML page that displays the image
        const previewWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
        
        if (previewWindow) {
            previewWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Screenshot Preview - ${title}</title>
                    <style>
                        body {
                            margin: 0;
                            padding: 20px;
                            background: #f5f5f5;
                            font-family: Arial, sans-serif;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            min-height: 100vh;
                        }
                        .header {
                            background: white;
                            padding: 15px 20px;
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                            margin-bottom: 20px;
                            text-align: center;
                            max-width: 800px;
                            width: 100%;
                        }
                        .header h1 {
                            margin: 0 0 10px 0;
                            color: #333;
                            font-size: 1.5em;
                        }
                        .header p {
                            margin: 0;
                            color: #666;
                            font-size: 0.9em;
                        }
                        .image-container {
                            background: white;
                            padding: 20px;
                            border-radius: 8px;
                            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                            text-align: center;
                            max-width: 90vw;
                            max-height: 80vh;
                            overflow: auto;
                        }
                        img {
                            max-width: 100%;
                            max-height: 70vh;
                            height: auto;
                            border-radius: 4px;
                            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                        }
                        .actions {
                            margin-top: 15px;
                            display: flex;
                            gap: 10px;
                            justify-content: center;
                        }
                        button {
                            padding: 8px 16px;
                            border: none;
                            border-radius: 4px;
                            cursor: pointer;
                            font-size: 0.9em;
                            transition: background-color 0.3s;
                        }
                        .btn-close {
                            background: #6c757d;
                            color: white;
                        }
                        .btn-close:hover {
                            background: #5a6268;
                        }
                        .btn-fullsize {
                            background: #007bff;
                            color: white;
                        }
                        .btn-fullsize:hover {
                            background: #0056b3;
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h1>📷 Screenshot Preview</h1>
                        <p>Feedback: ${title}</p>
                    </div>
                    <div class="image-container">
                        <img src="${blobUrl}" alt="Feedback Screenshot" />
                        <div class="actions">
                            <button class="btn-fullsize" onclick="toggleFullSize()">Toggle Full Size</button>
                            <button class="btn-close" onclick="window.close()">Close Preview</button>
                        </div>
                    </div>
                    <script>
                        let isFullSize = false;
                        function toggleFullSize() {
                            const img = document.querySelector('img');
                            if (isFullSize) {
                                img.style.maxWidth = '100%';
                                img.style.maxHeight = '70vh';
                                isFullSize = false;
                            } else {
                                img.style.maxWidth = 'none';
                                img.style.maxHeight = 'none';
                                isFullSize = true;
                            }
                        }
                        
                        // Handle window close cleanup
                        window.addEventListener('beforeunload', function() {
                            if (window.opener) {
                                // Notify parent window that preview is closing
                                try {
                                    window.opener.postMessage('preview-closed', '*');
                                } catch (e) {
                                    // Ignore cross-origin errors
                                }
                            }
                        });
                    </script>
                </body>
                </html>
            `);
            previewWindow.document.close();
        } else {
            alert('Pop-up blocked. Please allow pop-ups for this site to use the preview feature.');
        }
    };

    const fetchAdminData = async () => {
        try {
            setLoading(true);
            const authHeaders = getAuthHeaders();
            
            // Fetch stats
            const statsResponse = await fetch('/api/admin/stats', {
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });
            
            if (statsResponse.ok) {
                const statsData = await statsResponse.json();
                setStats(statsData);
            } else {
                console.error('Stats fetch failed:', statsResponse.status, statsResponse.statusText);
            }

            // Fetch feedback
            const feedbackResponse = await fetch('/feedback/', {
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });
            
            if (feedbackResponse.ok) {
                const feedbackData = await feedbackResponse.json();
                setFeedback(feedbackData);
                
                // Fetch image blobs for feedback with attachments
                for (const item of feedbackData) {
                    if (item.attachment_path) {
                        const filename = item.attachment_path.split('/').pop();
                        if (!imageBlobs[filename]) {
                            fetchImageBlob(filename);
                        }
                    }
                }
            } else {
                console.error('Feedback fetch failed:', feedbackResponse.status, feedbackResponse.statusText);
            }

            // Fetch users
            const usersResponse = await fetch('/api/admin/users', {
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });
            
            if (usersResponse.ok) {
                const usersData = await usersResponse.json();
                setUsers(usersData);
            } else {
                console.error('Users fetch failed:', usersResponse.status, usersResponse.statusText);
            }

            // Fetch matching rules
            const rulesResponse = await fetch('/matching-rules', {
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });
            
            if (rulesResponse.ok) {
                const rulesData = await rulesResponse.json();
                setMatchingRules(rulesData);
            } else {
                console.error('Matching rules fetch failed:', rulesResponse.status, rulesResponse.statusText);
            }
        } catch (err) {
            console.error('Admin data fetch error:', err);
            setError('Failed to load admin data: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFeedbackStatusUpdate = async (feedbackId, newStatus) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/feedback/${feedbackId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                // Refresh feedback list
                fetchAdminData();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update feedback status');
            }
        } catch (err) {
            setError('Failed to update feedback: ' + err.message);
        }
    };

    const handleUserStatusUpdate = async (userId, isActive) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({ is_active: isActive })
            });

            if (response.ok) {
                // Refresh users list
                fetchAdminData();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update user status');
            }
        } catch (err) {
            setError('Failed to update user: ' + err.message);
        }
    };

    const handleUserRoleUpdate = async (userId, newRole) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/api/admin/users/${userId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({ role: newRole })
            });

            if (response.ok) {
                // Refresh users list
                fetchAdminData();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update user role');
            }
        } catch (err) {
            setError('Failed to update user role: ' + err.message);
        }
    };

    const handlePasswordReset = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to reset the password for ${username}?`)) {
            return;
        }

        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/api/admin/users/${userId}/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });

            if (response.ok) {
                const data = await response.json();
                alert(`Password reset successfully!\n\nUsername: ${data.username}\nTemporary Password: ${data.temporary_password}\n\nPlease share this with the user securely.`);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to reset password');
            }
        } catch (err) {
            setError('Failed to reset password: ' + err.message);
        }
    };

    const handleImpersonate = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to impersonate ${username}?`)) {
            return;
        }

        try {
            const result = await startImpersonation(userId);
            if (result.success) {
                // Impersonation started successfully, the UI will automatically update
                alert(`Now impersonating ${username}. You will see their view of the system.`);
            } else {
                throw new Error(result.error);
            }
        } catch (err) {
            setError('Failed to start impersonation: ' + err.message);
        }
    };

    const handleMatchingRuleToggle = async (ruleId, isActive) => {
        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/matching-rules/${ruleId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({ is_active: isActive })
            });

            if (response.ok) {
                // Update the local state immediately for better UX
                setMatchingRules(prev => prev.map(rule => 
                    rule.rule_id === ruleId ? { ...rule, is_active: isActive } : rule
                ));
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update rule status');
            }
        } catch (err) {
            setError('Failed to update matching rule: ' + err.message);
        }
    };

    const handleMatchingRuleDelete = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this matching rule? This action cannot be undone.')) {
            return;
        }

        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch(`/matching-rules/${ruleId}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                }
            });

            if (response.ok) {
                // Remove from local state
                setMatchingRules(prev => prev.filter(rule => rule.rule_id !== ruleId));
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to delete rule');
            }
        } catch (err) {
            setError('Failed to delete matching rule: ' + err.message);
        }
    };

    const handleCreateMatchingRule = async () => {
        // Validate form
        if (!newRule.input_pattern.trim() || !newRule.mapped_value.trim()) {
            setError('Input pattern and mapped value are required');
            return;
        }

        try {
            const authHeaders = getAuthHeaders();
            const response = await fetch('/matching-rules', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeaders
                },
                body: JSON.stringify({
                    field: newRule.field_name,
                    input_pattern: newRule.input_pattern.trim(),
                    mapped_value: newRule.mapped_value.trim(),
                    rule_type: 'exact_match',
                    description: newRule.description.trim() || `Admin-created rule: '${newRule.input_pattern}' → '${newRule.mapped_value}'`
                })
            });

            if (response.ok) {
                const createdRule = await response.json();
                // Add to local state
                setMatchingRules(prev => [createdRule, ...prev]);
                // Reset form
                setNewRule({
                    field_name: 'projectName',
                    input_pattern: '',
                    mapped_value: '',
                    description: ''
                });
                setShowCreateRuleForm(false);
                setError(null);
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to create rule');
            }
        } catch (err) {
            setError('Failed to create matching rule: ' + err.message);
        }
    };

    const renderOverview = () => (
        <div className="overview-tab">
            <h3>Dashboard Overview</h3>
            {stats && (
                <div className="stats-grid">
                    <div className="stat-card">
                        <h4>Users</h4>
                        <div className="stat-value">{stats.users.total}</div>
                        <div className="stat-detail">
                            Active: {stats.users.active} | Admins: {stats.users.admins}
                        </div>
                    </div>
                    <div className="stat-card">
                        <h4>Feedback</h4>
                        <div className="stat-value">{stats.feedback.total}</div>
                        <div className="stat-detail">
                            New: {stats.feedback.new} | In Progress: {stats.feedback.in_progress}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    const renderFeedback = () => (
        <div className="feedback-tab">
            <h3>Feedback Management</h3>
            <div className="feedback-list">
                {feedback.map(item => (
                    <div key={item.id} className={`feedback-item ${item.status}`}>
                        <div className="feedback-header">
                            <h4>{item.title}</h4>
                            <div className="feedback-meta">
                                <span className={`type ${item.type}`}>{item.type.toUpperCase()}</span>
                                <span className={`priority ${item.priority}`}>{item.priority}</span>
                                <span className={`status ${item.status}`}>{item.status.replace('_', ' ')}</span>
                            </div>
                        </div>
                        <p className="feedback-description">{item.description}</p>
                        {item.attachment_path && (
                            <div className="feedback-attachment">
                                <label>📎 Screenshot:</label>
                                {(() => {
                                    const filename = item.attachment_path.split('/').pop();
                                    const blobUrl = imageBlobs[filename];
                                    
                                    if (blobUrl) {
                                        return (
                                            <div className="attachment-preview">
                                                <img 
                                                    src={blobUrl}
                                                    alt="Feedback screenshot"
                                                    className="feedback-screenshot"
                                                    onClick={() => previewAttachment(blobUrl, item.title)}
                                                    title="Click to preview screenshot"
                                                />
                                                <div className="attachment-actions">
                                                    <button
                                                        className="btn-preview"
                                                        onClick={() => previewAttachment(blobUrl, item.title)}
                                                        title="Preview in new tab"
                                                    >
                                                        👁️ Preview
                                                    </button>
                                                    <button
                                                        className="btn-download"
                                                        onClick={() => downloadAttachment(filename, item.title)}
                                                        title="Download screenshot"
                                                    >
                                                        ⬇️ Download
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <div className="loading-image">
                                                📷 Loading screenshot...
                                            </div>
                                        );
                                    }
                                })()}
                            </div>
                        )}
                        <div className="feedback-actions">
                            <select 
                                value={item.status} 
                                onChange={(e) => handleFeedbackStatusUpdate(item.id, e.target.value)}
                            >
                                <option value="new">New</option>
                                <option value="in_progress">In Progress</option>
                                <option value="resolved">Resolved</option>
                                <option value="closed">Closed</option>
                            </select>
                            <span className="feedback-date">
                                {new Date(item.created_at).toLocaleDateString()}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderUsers = () => (
        <div className="users-tab">
            <h3>User Management</h3>
            <div className="users-table">
                <table>
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Created</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.user_id} className={!user.is_active ? 'inactive' : ''}>
                                <td>{user.username}</td>
                                <td>{user.email}</td>
                                <td>{user.first_name} {user.last_name}</td>
                                <td>
                                    <select 
                                        value={user.role} 
                                        onChange={(e) => handleUserRoleUpdate(user.user_id, e.target.value)}
                                        className={`role-select ${user.role}`}
                                    >
                                        <option value="user">User</option>
                                        <option value="reviewer">Reviewer</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </td>
                                <td>
                                    <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                                        {user.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                                <td>
                                    <div className="user-actions">
                                        <button 
                                            onClick={() => handleUserStatusUpdate(user.user_id, !user.is_active)}
                                            className={user.is_active ? 'deactivate-btn' : 'activate-btn'}
                                        >
                                            {user.is_active ? 'Deactivate' : 'Activate'}
                                        </button>
                                        <button 
                                            onClick={() => handlePasswordReset(user.user_id, user.username)}
                                            className="reset-password-btn"
                                        >
                                            Reset Password
                                        </button>
                                        <button 
                                            onClick={() => handleImpersonate(user.user_id, user.username)}
                                            className="impersonate-btn"
                                        >
                                            Impersonate
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderMatchingRules = () => (
        <div className="matching-rules-tab">
            <div className="matching-rules-header">
                <div className="header-content">
                    <h3>Matching Rules Management</h3>
                    <p>Manage automatic field mapping rules created by users during document processing.</p>
                </div>
                <button 
                    className="btn-create-rule"
                    onClick={() => setShowCreateRuleForm(true)}
                >
                    ➕ Create New Rule
                </button>
            </div>

            {showCreateRuleForm && (
                <div className="create-rule-form">
                    <div className="form-header">
                        <h4>Create New Matching Rule</h4>
                        <button 
                            className="btn-close"
                            onClick={() => {
                                setShowCreateRuleForm(false);
                                setNewRule({
                                    field_name: 'projectName',
                                    input_pattern: '',
                                    mapped_value: '',
                                    description: ''
                                });
                                setError(null);
                            }}
                        >
                            ✕
                        </button>
                    </div>
                    
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Field Type:</label>
                            <select 
                                value={newRule.field_name}
                                onChange={(e) => setNewRule(prev => ({...prev, field_name: e.target.value}))}
                            >
                                <option value="projectName">Project Name</option>
                                <option value="contractorName">Contractor Name</option>
                                <option value="subcontractorName">Subcontractor Name</option>
                            </select>
                        </div>
                        
                        <div className="form-group">
                            <label>Input Pattern:</label>
                            <input 
                                type="text"
                                placeholder="e.g., 'Southward Village'"
                                value={newRule.input_pattern}
                                onChange={(e) => setNewRule(prev => ({...prev, input_pattern: e.target.value}))}
                            />
                            <small>What users typically type or enter</small>
                        </div>
                        
                        <div className="form-group">
                            <label>Mapped Value:</label>
                            <input 
                                type="text"
                                placeholder="e.g., 'Southward Village / Latimer Crossing - DEMO'"
                                value={newRule.mapped_value}
                                onChange={(e) => setNewRule(prev => ({...prev, mapped_value: e.target.value}))}
                            />
                            <small>What it should automatically map to</small>
                        </div>
                        
                        <div className="form-group full-width">
                            <label>Description (Optional):</label>
                            <input 
                                type="text"
                                placeholder="Optional description for this rule"
                                value={newRule.description}
                                onChange={(e) => setNewRule(prev => ({...prev, description: e.target.value}))}
                            />
                        </div>
                    </div>
                    
                    <div className="form-actions">
                        <button 
                            className="btn-save"
                            onClick={handleCreateMatchingRule}
                        >
                            Create Rule
                        </button>
                        <button 
                            className="btn-cancel"
                            onClick={() => setShowCreateRuleForm(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
            
            {matchingRules.length === 0 ? (
                <div className="no-data">
                    <p>No matching rules have been created yet.</p>
                    <p>Rules are automatically created when users select suggestions or manually map field values.</p>
                </div>
            ) : (
                <div className="rules-table-container">
                    <table className="rules-table">
                        <thead>
                            <tr>
                                <th>Field</th>
                                <th>Input Pattern</th>
                                <th>Mapped Value</th>
                                <th>Type</th>
                                <th>Created</th>
                                <th>Created By</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {matchingRules.map(rule => (
                                <tr key={rule.rule_id} className={!rule.is_active ? 'inactive' : ''}>
                                    <td>
                                        <span className={`field-badge field-${rule.field_name}`}>
                                            {rule.field_name === 'projectName' ? 'Project' : 
                                             rule.field_name === 'contractorName' ? 'Contractor' : 
                                             'Subcontractor'}
                                        </span>
                                    </td>
                                    <td className="pattern-cell">
                                        <code>"{rule.input_pattern}"</code>
                                    </td>
                                    <td className="mapped-cell">
                                        <strong>"{rule.mapped_value}"</strong>
                                    </td>
                                    <td>{rule.rule_type}</td>
                                    <td>{new Date(rule.created_date).toLocaleDateString()}</td>
                                    <td>{rule.created_by}</td>
                                    <td>
                                        <span className={`status-badge ${rule.is_active ? 'active' : 'inactive'}`}>
                                            {rule.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            className={`btn-toggle ${rule.is_active ? 'btn-disable' : 'btn-enable'}`}
                                            onClick={() => handleMatchingRuleToggle(rule.rule_id, !rule.is_active)}
                                            title={rule.is_active ? 'Disable rule' : 'Enable rule'}
                                        >
                                            {rule.is_active ? 'Disable' : 'Enable'}
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => handleMatchingRuleDelete(rule.rule_id)}
                                            title="Delete rule"
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );

    if (loading) return <div className="loading">Loading admin data...</div>;
    if (error) return <div className="error">Error: {error}</div>;

    return (
        <div className="admin-dashboard">
            <h2>Admin Dashboard</h2>
            
            <div className="admin-tabs">
                <button 
                    className={activeTab === 'overview' ? 'active' : ''}
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={activeTab === 'feedback' ? 'active' : ''}
                    onClick={() => setActiveTab('feedback')}
                >
                    Feedback ({feedback.length})
                </button>
                <button 
                    className={activeTab === 'users' ? 'active' : ''}
                    onClick={() => setActiveTab('users')}
                >
                    Users ({users.length})
                </button>
                <button 
                    className={activeTab === 'matching-rules' ? 'active' : ''}
                    onClick={() => setActiveTab('matching-rules')}
                >
                    Matching Rules ({matchingRules.length})
                </button>
            </div>

            <div className="admin-content">
                {activeTab === 'overview' && renderOverview()}
                {activeTab === 'feedback' && renderFeedback()}
                {activeTab === 'users' && renderUsers()}
                {activeTab === 'matching-rules' && renderMatchingRules()}
            </div>
        </div>
    );
};

export default AdminDashboard;